import { useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { StaffWorkspaceContext, useAccount, type StaffWorkspace } from "../../app/session";
import { Coco } from "../../components/States";
import { CocoLugt, RundgangBlase } from "../../components/CocoRundgang";
import { nurLesenSatz } from "../../app/nurLesen";
import { useTarifKatalog, useTarifStand } from "../../components/Tarif";
import { modulGesperrt, tarifMitModul } from "../../model/tarif";
import { toast } from "../../components/Toast";
import { api } from "../../data";
import { fehlerText } from "../../lib/fehler";
import { currentLang, t } from "../../lib/sprache";
import { useOnboardingPrefs } from "../creator/Einfuehrung";
import { agenturGuideFor, agenturGuideText, studioGuideText } from "./einfuehrung-texte";
import { cocoUiText, leereListe, seitenKnopf, stationText } from "./coco-rundgang-texte";
import {
  FREIGABE_MODUL, STATION_MODUL, beispielAnbieten, ereignisArgs, fortsetzSchritt, lugtStoert, rolleFuer, rundgangEnde, rundgangJetzt, rundgangMitBeispiel, rundgangPatch, rundgangSchritt,
  rundgangStand, rundgangStarten, seitenZiel, sollAutoStarten, stationenFuer, useRundgang, type BeispielStand, type Station,
} from "./coco-rundgang";
import { legeBeispieldatenAn, loescheBeispieldaten, merke } from "./schnellstart-daten";
import { useStartSchritte } from "./Startleitfaden";

/**
 * Coco im Agentur-Portal (docs/COCO.md).
 *  – „Coco erklärt“: Karte unten rechts beim ersten Besuch einer Hauptseite (was passiert hier, was legst du als Erstes an; bei
 *    leerer Liste „Zeig’s mir mit Beispieldaten“). „Verstanden“ blendet sie für diese Seite aus, „Einführung ausschalten“ für alle
 *    Seiten (gemeinsam mit dem Creator-Portal, intro_off). Danach holt das Coco-Symbol unten rechts sie jederzeit zurück.
 *  – Rundgang mit Coco (20.10.2026): beim ersten Login von selbst, sonst über „Hilfe & Support“ bzw. die Karte der Übersicht.
 */

/** Stand der Beispieldaten; derselbe Schlüssel wie die Weiche der Übersicht, damit nur einmal gefragt wird */
function useBeispielStand(ws: StaffWorkspace | null): BeispielStand | null {
  const q = useQuery({
    queryKey: ["schnellstart", ws?.org.org_id, "weiche"], enabled: !!ws && ws.isChef && !ws.isCreator, staleTime: 30_000, retry: false,
    queryFn: () => api.read<BeispielStand | string>("sample_data_status", { p_org: ws!.org.org_id })
      .then((r) => (typeof r === "string" ? JSON.parse(r) as BeispielStand : r)),
  });
  return q.data ?? null;
}

/**
 * „Zeig’s mir mit Beispieldaten“: Beispieldaten wie im Schnellstart anlegen (0325), dann dort ansehen. K-573: im Rundgang
 * (imRundgang) ohne Sprung in den Schnellstart; die Stationen zeigen danach gefüllte Seiten. entfernen: sample_data_delete.
 */
function useBeispieldaten(ws: StaffWorkspace | null) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const start = async (imRundgang = false): Promise<boolean> => {
    if (!ws) return false;
    setBusy(true);
    try {
      await legeBeispieldatenAn(api, ws.org.org_id, { orgName: ws.org.name, language: currentLang() === "en" ? "en" : "de", today: new Date().toISOString().slice(0, 10) });
      // im Rundgang alle Listen neu laden: die nächsten Stationen zeigen die Beispiel-Creator, -Kooperation, -Vertrag und -Rechnung
      await (imRundgang ? qc.invalidateQueries() : qc.invalidateQueries({ queryKey: ["schnellstart", ws.org.org_id] }));
      toast(cocoUiText(t).beispieldatenDa);
      if (!imRundgang) navigate("/agentur/schnellstart");
      return true;
    } catch (e) {
      toast(fehlerText(e), "alert");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const entfernen = async (): Promise<boolean> => {
    if (!ws) return false;
    setBusy(true);
    try {
      await loescheBeispieldaten(api, ws.org.org_id);
      await qc.invalidateQueries();
      toast(cocoUiText(t).beispielEntfernt);
      return true;
    } catch (e) {
      toast(fehlerText(e), "alert");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, start, entfernen };
}

export function AgenturEinfuehrung({ nurLesen = false }: { nurLesen?: boolean }) {
  const { pathname } = useLocation();
  const { account, session } = useAccount();
  const userId = account?.session.userId ?? session?.userId ?? null;
  const titleId = useId();
  const { onboarding, ready, save } = useOnboardingPrefs(userId);
  const [hidden, setHidden] = useState<string | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const ws = useContext(StaffWorkspaceContext);
  const lauf = useRundgang();
  const stand = useBeispielStand(ws);
  const beispiel = useBeispieldaten(ws);
  const roh = agenturGuideFor(pathname);
  // Einstellungen erklärt die Karte nur Admins (für das Team ist dort das eigene Profil)
  const key = roh === "einstellungen" && !ws?.isChef ? null : roh;
  const pref = key ? `agentur_${key}` : null;
  const embedded = typeof document !== "undefined" && document.documentElement.dataset.embed === "true";
  const gesehen = !!pref && !!(onboarding.guides as Record<string, boolean> | undefined)?.[pref];
  const grundsaetzlich = !!key && !!pref && ready && !embedded && !onboarding.intro_off && !lauf;
  const show = grundsaetzlich && (offen === pref || (!gesehen && hidden !== pref));
  // Solange die Karte offen ist, hält der Inhalt Platz frei (ab 1600 px rechts daneben, sonst unten), damit sie nichts dauerhaft verdeckt
  const creatorKonto = !!ws?.isCreator;
  const karte = useRef<HTMLElement>(null);
  useEffect(() => setOffen(null), [pathname]);
  useLayoutEffect(() => {
    if (!show) return;
    const root = document.documentElement;
    root.classList.add("tk-guide-agentur-open");
    // Höhe der Karte für die Luft unter dem Inhalt (app.css, unter 1600 px liegt die Karte über dem Inhalt)
    const h = karte.current?.offsetHeight;
    if (h) root.style.setProperty("--tk-guide-h", `${h}px`);
    // Nachtest CO-011: zwischen 768 und 1599 px liegt die Karte oben rechts; sie beginnt unter dem Seitenkopf, damit dessen
    // Hauptaktionen (etwa „Vertrag erstellen“) frei bleiben (kompakt.css, --tk-guide-top)
    const kopf = document.querySelector("main .pagehead")?.getBoundingClientRect();
    if (kopf) root.style.setProperty("--tk-guide-top", `${Math.ceil(kopf.bottom + window.scrollY + 12)}px`);
    return () => { root.classList.remove("tk-guide-agentur-open"); root.style.removeProperty("--tk-guide-h"); root.style.removeProperty("--tk-guide-top"); };
  });
  const done = (off: boolean) => {
    if (!pref) return;
    setHidden(pref);
    setOffen(null);
    void save(off ? { intro_off: true } : { guides: { [pref]: true } as never }).catch(() => undefined);
  };
  useEffect(() => {
    if (!show || !pref) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") done(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });
  if (!grundsaetzlich || !key || !pref) return null;
  const ui = cocoUiText(t);
  if (!show) return <CocoLugt label={ui.lugt} onClick={() => setOffen(pref)} stoertAmHandy={lugtStoert(pathname)} />;
  const g = creatorKonto ? studioGuideText(key, t) : agenturGuideText(key, t);
  const chef = !!ws?.isChef;
  const ziel = seitenZiel(key, { chef, studio: creatorKonto, betraege: !!ws && (ws.isChef || ws.can("view_amounts")), pfad: pathname });
  const knopf = ziel ? seitenKnopf(key, t, { studio: creatorKonto }) : null;
  const leer = beispielAnbieten(key, { chef, studio: creatorKonto, nurLesen }, stand) ? leereListe(key, t) : null;
  const rolle = ws ? rolleFuer(ws) : null;
  return (
    <aside ref={karte} className="tk-guide tk-guide-agentur panel stack" role="dialog" aria-modal="false" aria-labelledby={titleId}>
      <div className="tk-guide-kopf">
        <Coco pose="erklaert" size={56} eager />
        <div className="stack">
          <div className="eyebrow">{ui.seitenEyebrow}</div>
          <h2 id={titleId}>{g.title}</h2>
        </div>
      </div>
      <p>{g.text}</p>
      <ul>{g.bullets.map((b) => <li key={b}>{b}</li>)}</ul>
      {leer && (
        <div className="tk-guide-leer stack">
          <p>{leer}</p>
          <div className="row"><button type="button" disabled={beispiel.busy} aria-busy={beispiel.busy} onClick={() => { done(false); void beispiel.start(); }}>{beispiel.busy ? ui.beispieldatenLaeuft : ui.beispieldaten}</button></div>
        </div>
      )}
      <div className="row">
        <button type="button" className="primary" onClick={() => done(false)}>{ui.verstanden}</button>
        {/* 0425: in Pause und Nur-Lesen kein „Rechnung erstellen“ o. ä., sondern der Hinweis */}
        {knopf && ziel && !nurLesen && <Link className="btn" to={ziel} onClick={() => done(false)}>{knopf}</Link>}
        {knopf && ziel && nurLesen && <span className="pill" title={nurLesenSatz()}>{t("Nur lesen", "Read-only")}</span>}
        {key === "overview" && rolle && !nurLesen && <button type="button" onClick={() => { done(false); rundgangStarten(rolle); }}>{ui.rundgangStarten}</button>}
        <button type="button" className="quiet" onClick={() => done(true)}>{ui.ausschalten}</button>
      </div>
    </aside>
  );
}

/**
 * Rundgang im Agentur-Portal: Admin (9 Stationen), Team ohne Admin (4) und eigenes Creator-Konto (7, K-557). Startet beim ersten Login von selbst
 * (sollAutoStarten), sonst über rundgangStarten (Hilfe & Support, Karte der Übersicht). Gespeichert wird beim Start, je Schritt
 * (K-591: nach dem Neuladen geht es dort weiter, fortsetzSchritt) und am Ende, nacheinander, damit ein später Schritt das Ende nicht
 * überschreibt; gemessen werden Start, Ende und Überspringen (0380, nur Zählwerte).
 */
export function CocoRundgangAgentur({ ws, nurLesen = false }: { ws: StaffWorkspace; nurLesen?: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { account, session } = useAccount();
  const userId = account?.session.userId ?? session?.userId ?? null;
  const { onboarding, ready, save } = useOnboardingPrefs(userId);
  const lauf = useRundgang();
  const stand = useBeispielStand(ws);
  const beispiel = useBeispieldaten(ws);
  const rolle = rolleFuer(ws);
  const stationen = rolle && rolle !== "creator" ? stationenFuer(rolle, { betraege: ws.isChef || ws.can("view_amounts"), auszahlungen: ws.can("trigger_payouts") }) : [];
  const embedded = typeof document !== "undefined" && document.documentElement.dataset.embed === "true";
  const auto = useRef(false);
  const gemeldet = useRef(0);
  // K-591: Speichern nacheinander (Schritt, dann Ende), nie überholend
  const kette = useRef<Promise<unknown>>(Promise.resolve());
  const speichern = (patch: Parameters<typeof save>[0]) => { kette.current = kette.current.then(() => save(patch)).catch(() => undefined); };
  // K-551: „(ab Team)“ an Stationen, deren Modul der Tarif nicht hat; solange der Tarifstand lädt, ohne Zusatz
  const tarif = useTarifStand(lauf ? ws.org.org_id : null);
  const katalog = useTarifKatalog();
  // K-557: Abschluss führt zum nächsten offenen Punkt der Startliste (dieselbe Reihenfolge wie der Rundgang)
  const start = useStartSchritte(ws);
  // K-557: eigenes Creator-Konto startet wie der Admin nur ohne eigenen Deal von selbst (Beispieldaten gibt es dort nicht)
  const studioDeals = useQuery({ queryKey: ["coco-studio-deals", ws.org.org_id], enabled: !!ws.isCreator && ws.isChef, staleTime: 30_000, retry: false,
    queryFn: () => api.select<{ id: string }>("deals", { columns: "id", filters: [["org_id", "eq", ws.org.org_id]], limit: 1 }).then((r) => r.length) });
  const ohneKooperation = ws.isCreator ? (studioDeals.data === undefined ? null : studioDeals.data === 0) : stand ? stand.deals === 0 : null;
  const freigabeAb = tarif.data && modulGesperrt(tarif.data.limits, FREIGABE_MODUL) ? tarifMitModul(FREIGABE_MODUL, katalog.data, !!ws.isCreator) : null;
  const mitBeispiel = rolle === "admin" && beispielAnbieten(null, { chef: ws.isChef, studio: !!ws.isCreator, nurLesen }, stand);
  // K-573: in Schritt 1 Beispieldaten gewählt und noch da: der Abschluss fragt „behalten oder entfernen?“
  const beispielGewaehlt = rolle === "admin" && !!lauf?.beispiel && !!stand?.has_sample;
  const stationTitel = (s: Station, info: { leer: boolean }) => {
    const x = stationText(s.id, t, { freigabeAb, beispiel: mitBeispiel, beispielGewaehlt, beispielDa: rolle === "admin" && !!stand?.has_sample, liste: start.sichtbar, leer: info.leer });
    const modul = STATION_MODUL[s.id];
    if (!modul || !tarif.data || !modulGesperrt(tarif.data.limits, modul)) return x;
    const ab = tarifMitModul(modul, katalog.data);
    return ab ? { ...x, titel: t(`${x.titel} (ab ${ab})`, `${x.titel} (from ${ab})`) } : x;
  };

  // 0425 (S-6b): in Pause und Nur-Lesen keinen Rundgang. Ein laufender (auch aus dem Browser-Speicher fortgesetzter) Rundgang zog
  // jede aufgerufene Seite auf die Station „/agentur“ zurück; der Rahmen meldet nurLesen, solange der Lebenszyklus lädt
  useEffect(() => {
    if (nurLesen && lauf) rundgangEnde();
  }, [nurLesen, !!lauf]);

  // Erster Login: von selbst starten (einmal je Sitzung); K-591: nach dem Neuladen mitten im Rundgang beim gespeicherten Schritt weiter
  useEffect(() => {
    if (auto.current || rundgangJetzt() || !ready || nurLesen) return;
    const weiterBei = fortsetzSchritt({ rolle, onboarding, anzahl: stationen.length, stationPfade: stationen.map((x) => x.pfad), pfad: pathname, jetzt: Date.now(), nurLesen, eingebettet: embedded });
    if (weiterBei !== null) {
      auto.current = true;
      rundgangStarten(rolle!, "resume", { schritt: weiterBei, beispiel: rolle === "admin" && !!rundgangStand(onboarding, rolle)?.beispiel });
      return;
    }
    if (sollAutoStarten({ rolle, onboarding, pfad: pathname, nurLesen, eingebettet: embedded, ohneKooperation })) {
      auto.current = true;
      rundgangStarten(rolle!, "auto");
    }
  }, [ready, pathname, ohneKooperation, rolle, nurLesen]);

  // Stand je Schritt merken (K-591), den Start einmal je Lauf messen (nicht beim Fortsetzen nach dem Neuladen)
  useEffect(() => {
    if (!lauf || !rolle || lauf.rolle !== rolle || !stationen.length) return;
    speichern(rundgangPatch(onboarding, rolle, stationen, "laufend", Math.min(lauf.schritt, stationen.length - 1), new Date().toISOString(), { beispiel: !!lauf.beispiel }));
    if (lauf.nr === gemeldet.current) return;
    gemeldet.current = lauf.nr;
    if (lauf.quelle !== "resume") void api.read("coco_tour_log", ereignisArgs(ws.org.org_id, rolle, "started", null, stationen.length)).catch(() => undefined);
  }, [lauf?.nr, lauf?.schritt, lauf?.beispiel]);

  // Rundgang einer anderen Rolle (Rollenwechsel, Agenturwechsel) oder ohne Stationen: beenden
  useEffect(() => {
    if (lauf && (!rolle || lauf.rolle !== rolle || stationen.length === 0)) rundgangEnde();
  }, [lauf?.nr, rolle]);

  if (!lauf || !rolle || lauf.rolle !== rolle || !stationen.length) return null;
  const index = Math.min(lauf.schritt, stationen.length - 1);
  const ende = (status: "beendet" | "uebersprungen", i: number) => {
    // K-573: der Abschluss zeigt die Liste „So startest du“ auf der Übersicht; nach „Fertig“ bleibt die Übersicht stehen, statt
    // dass die Weiche (Uebersicht.tsx) gleich in den Schnellstart springt (wie „Später, zur Übersicht“ im Schnellstart)
    if (status === "beendet" && rolle === "admin") merke(ws.org.org_id, "spaeter");
    rundgangEnde();
    speichern(rundgangPatch(onboarding, rolle, stationen, status, i, new Date().toISOString()));
    void api.read("coco_tour_log", ereignisArgs(ws.org.org_id, rolle, status === "beendet" ? "finished" : "skipped", status === "beendet" ? stationen.length : i + 1, stationen.length)).catch(() => undefined);
  };
  const ui = cocoUiText(t);
  const naechster = start.schritte.find((x) => !x.done);
  // K-573: Schritt 1 bietet Beispieldaten als Standard an (nur Admin, nicht im Creator-Konto, nicht Nur-Lesen, nur ohne Beispiel-
  // und echte Daten: beispielAnbieten), daneben „Ohne Beispiele“; mit Beispieldaten zeigen die Stationen gefüllte Seiten
  const wahl = mitBeispiel && index === 0 ? (
    <>
      <button type="button" disabled={beispiel.busy} onClick={() => rundgangSchritt(1)}>{ui.ohneBeispiel}</button>
      <button type="button" className="primary" disabled={beispiel.busy} aria-busy={beispiel.busy}
        onClick={() => { void beispiel.start(true).then((ok) => { if (ok && rundgangJetzt()?.nr === lauf.nr) { rundgangMitBeispiel(); rundgangSchritt(1); } }); }}>
        {beispiel.busy ? ui.beispieldatenLaeuft : ui.mitBeispiel}
      </button>
    </>
  ) : undefined;
  // K-573: Abschluss auf der Übersicht neben der Liste „So startest du“; nach Beispieldaten aus Schritt 1 „behalten oder entfernen?“
  const abschluss = beispielGewaehlt ? (
    <>
      <button type="button" disabled={beispiel.busy} aria-busy={beispiel.busy} onClick={() => { void beispiel.entfernen().then((ok) => { if (ok) ende("beendet", index); }); }}>
        {beispiel.busy ? ui.beispielEntfernenLaeuft : ui.beispielEntfernen}
      </button>
      <button type="button" className="primary" disabled={beispiel.busy} onClick={() => ende("beendet", index)}>{ui.beispielBehalten}</button>
    </>
  ) : (rolle === "admin" || rolle === "studio") && ((start.sichtbar && naechster) || mitBeispiel) ? (
    <>
      {mitBeispiel && (
        <button type="button" disabled={beispiel.busy} aria-busy={beispiel.busy} onClick={() => { ende("beendet", index); void beispiel.start(); }}>
          {beispiel.busy ? ui.beispieldatenLaeuft : ui.beispieldaten}
        </button>
      )}
      {start.sichtbar && naechster && <button type="button" className="primary tk-rundgang-haupt-zeile" onClick={() => { ende("beendet", index); navigate(naechster.to); }}>{ui.naechsterSchritt}</button>}
    </>
  ) : undefined;
  return (
    <RundgangBlase key={lauf.nr} stationen={stationen} index={index} t={t} text={stationTitel} onSchritt={rundgangSchritt} onEnde={ende}
      abschluss={abschluss} ohneFertig={beispielGewaehlt} fertigPrimaer={!beispielGewaehlt && !(start.sichtbar && naechster)}
      wahl={wahl} wahlHinweis={ui.beispielWahlHinweis} />
  );
}
